import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../injection-tokens/entity-service-tokens';
import { IClassesEntityService } from '../../interfaces/entities/classes-entity-service.interface';

export interface DeleteConfirmDialogData {
  subscriptionId: string;
  displayName?: string;
}

@Component({
  selector: 'app-delete-confirm-dialog',
  template: `
    <h2 mat-dialog-title>
      <mat-icon color="warn" style="vertical-align: middle; margin-right: 8px;">warning</mat-icon>
      Delete Template Subscription
    </h2>

    <mat-dialog-content>
      <!-- ── Block state: 409 active VMs ── -->
      <ng-container *ngIf="blockMessage; else confirmForm">
        <div class="block-panel">
          <mat-icon color="warn" class="block-icon">block</mat-icon>
          <div>
            <p class="block-title">Cannot delete this subscription</p>
            <p class="block-body">{{ blockMessage }}</p>
          </div>
        </div>
      </ng-container>

      <!-- ── Normal confirm form ── -->
      <ng-template #confirmForm>
        <p class="confirm-desc">
          This will permanently remove
          <strong>{{ data.displayName || data.subscriptionId }}</strong>
          from the provisioning pool.
          Future org-template creation requests matching this subscription will fail
          until a replacement is added.
        </p>
        <p class="confirm-prompt">
          Type <code class="delete-code">DELETE</code> to confirm:
        </p>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Confirmation phrase</mat-label>
          <input matInput [(ngModel)]="confirmText" placeholder="DELETE"
                 (keyup.enter)="submit()" autocomplete="off" />
        </mat-form-field>
      </ng-template>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <ng-container *ngIf="blockMessage; else normalActions">
        <button mat-button (click)="cancel()">Close</button>
      </ng-container>
      <ng-template #normalActions>
        <button mat-button type="button" (click)="cancel()">Cancel</button>
        <button mat-raised-button color="warn" type="button"
                [disabled]="confirmText !== 'DELETE' || deleting"
                (click)="submit()">
          <mat-spinner diameter="18" *ngIf="deleting"
                       style="display:inline-block; margin-right: 6px;"></mat-spinner>
          {{ deleting ? 'Deleting…' : 'Delete' }}
        </button>
      </ng-template>
    </mat-dialog-actions>
  `,
  styles: [`
    mat-dialog-content { min-width: 420px; }
    .confirm-desc { margin-bottom: 12px; line-height: 1.5; }
    .confirm-prompt { font-weight: 600; margin-bottom: 8px; }
    .delete-code {
      background: #fff3e0; color: #e65100;
      padding: 2px 6px; border-radius: 4px; font-family: monospace;
    }
    .full-width { width: 100%; }
    .block-panel {
      display: flex; align-items: flex-start; gap: 12px;
      background: #fef3f2; border: 1px solid #fca5a5;
      border-radius: 8px; padding: 16px;
    }
    .block-icon { font-size: 32px; height: 32px; width: 32px; flex-shrink: 0; }
    .block-title { font-weight: 600; margin: 0 0 6px; color: #c0392b; }
    .block-body { margin: 0; font-size: 14px; line-height: 1.5; }
  `]
})
export class DeleteConfirmDialogComponent {
  confirmText = '';
  deleting = false;
  blockMessage: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<DeleteConfirmDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DeleteConfirmDialogData,
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private classService: IClassesEntityService
  ) {}

  submit(): void {
    if (this.confirmText !== 'DELETE' || this.deleting) return;

    this.deleting = true;
    this.classService.deleteTemplateSubscription(this.data.subscriptionId).subscribe({
      next: () => {
        this.deleting = false;
        this.dialogRef.close(true);
      },
      error: (err: any) => {
        this.deleting = false;
        if (err.status === 409) {
          this.blockMessage =
            err.error?.message ??
            'This subscription has active org-template VMs. Delete all templates on this subscription first.';
        } else {
          this.dialogRef.close(false);
        }
      }
    });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
